import type {
  AppNotificationOptions,
  AppNotificationOptionsByType,
  AppNotificationType,
  AppNotify,
} from "./app-notification.type";
import type { NotificationArgsProps } from "antd";

import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import AppNotificationText from "./app-notification-text";
import { useAppNotificationContext } from "./app-notification.context";

export const useAppNotification = (): { notify: AppNotify } => {
  const { t } = useTranslation();

  const { api } = useAppNotificationContext();

  const buildOptions = (
    type: AppNotificationType,
    options?: AppNotificationOptions
  ): AppNotificationOptions => {
    const DEFAULT_OPTIONS: NotificationArgsProps = {
      duration: 4,
      title: "",
      classNames: {
        root: "app-notification",
        wrapper: "app-notification--wrapper",
        icon: "app-notification--icon",
        section: "app-notification--section",
        title: "app-notification--title",
        description: "app-notification--description",
        actions: "app-notification--actions",
      },
    };

    const DEFAULT_OPTIONS_BY_TYPES: AppNotificationOptionsByType = {
      error: {
        title: t("form:error:genericError"),
      },
    };

    return {
      ...DEFAULT_OPTIONS,
      ...(type && (DEFAULT_OPTIONS_BY_TYPES[type] ?? {})),
      ...(options ?? {}),
    };
  };

  const notify = (
    type: AppNotificationType = "open",
    options?: AppNotificationOptions
  ) => {
    const {
      title,
      titleRows,
      description,
      descriptionRows,
      actions,
      ...othersOptions
    } = buildOptions(type, options);

    api[type]({
      title: (
        <AppNotificationText rows={titleRows}>{title}</AppNotificationText>
      ),
      description: (
        <AppNotificationText rows={descriptionRows}>
          {description}
        </AppNotificationText>
      ),
      actions: actions ? (
        <Flex gap="small" align="center">
          {actions}
        </Flex>
      ) : undefined,
      ...othersOptions,
    });
  };

  return {
    notify: {
      open: (options?: AppNotificationOptions) => notify("open", options),
      success: (options?: AppNotificationOptions) => notify("success", options),
      info: (options?: AppNotificationOptions) => notify("info", options),
      warning: (options?: AppNotificationOptions) => notify("warning", options),
      error: (options?: AppNotificationOptions) => notify("error", options),
      close: (key?: string) => api.destroy(key),
    },
  };
};
